// V14.9 — KPI / Objective notes modal with ICS Section / Role / Comments /
// Attachments. V14.9.1 — when the KPI carries imported `section_evaluations`,
// the Section dropdown shows them as the options (with the evaluation question
// + "what good looks like" displayed in a context panel above the form).
// V14.13.5 — Always show canonical ICS Sections in the primary dropdown.
// Imported `section_evaluations` are used as a SECONDARY "Evaluation Question"
// picker that only appears when the chosen ICS section has matching eval slots.
// This fixes the "(unnamed section)" bug caused by imported rows with empty
// or non-canonical section labels.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, Paperclip, Send, ClipboardCheck, Target } from 'lucide-react';
import { toast } from 'sonner';
import { objectivesApi, uploadsApi } from '../../lib/api';

const DEFAULT_SECTIONS = ['Command', 'Operations', 'Planning', 'Logistics', 'Finance', 'BRT', 'Others'];

// Normalize an imported section label (e.g. "CMD-LO", "OPS", "operations") to
// one of the canonical ICS sections. Returns null if no match.
const normalizeToICS = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase();
  if (!s) return null;
  if (s.startsWith('cmd') || s === 'command') return 'Command';
  if (s.startsWith('ops') || s.startsWith('operation')) return 'Operations';
  if (s.startsWith('plan')) return 'Planning';
  if (s.startsWith('log')) return 'Logistics';
  if (s.startsWith('fin') || s.startsWith('admin')) return 'Finance';
  if (s.startsWith('brt')) return 'BRT';
  return null;
};

const fileUrl = (fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${fileId}`;

export default function ObjectiveKPINotesModal({ open, onOpenChange, exerciseId, objective, kpi, initialEvalId = null, readOnly = false }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [section, setSection] = useState('');
  const [sectionOther, setSectionOther] = useState('');
  const [evalId, setEvalId] = useState(''); // selected section_evaluation id
  const [role, setRole] = useState('');
  const [files, setFiles] = useState([]);

  // V14.13.5 — Section dropdown is ALWAYS the canonical ICS list.
  // section_evaluations are only used as a secondary "Evaluation Question"
  // picker filtered to the current section.
  const sectionEvals = kpi?.section_evaluations || [];
  const evalsForSection = section
    ? sectionEvals.filter(ev => normalizeToICS(ev.section) === section)
    : [];
  const currentEval = evalId ? sectionEvals.find(ev => ev.eval_id === evalId) : null;

  const loadNotes = useCallback(async () => {
    if (!objective?.objective_id) return;
    setLoading(true);
    try {
      const data = await objectivesApi.listNotes(objective.objective_id, kpi?.kpi_id);
      setNotes(data || []);
    } catch {
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [objective?.objective_id, kpi?.kpi_id]);

  useEffect(() => {
    if (open) {
      setMessage(''); setSectionOther(''); setRole('');
      setFiles([]);
      // V14.13.5 — If a specific eval row was passed in, pre-select both its
      // canonical ICS section and the eval slot.
      if (initialEvalId) {
        const ev = (kpi?.section_evaluations || []).find(e => e.eval_id === initialEvalId);
        const ics = ev ? normalizeToICS(ev.section) : null;
        setSection(ics || '');
        setEvalId(initialEvalId);
      } else {
        setSection('');
        setEvalId('');
      }
      loadNotes();
    }
  }, [open, loadNotes, initialEvalId, kpi]);

  // Clear the eval picker whenever the user changes the section (so a stale
  // eval_id from another section never sneaks into the payload).
  useEffect(() => {
    if (evalId) {
      const ev = sectionEvals.find(e => e.eval_id === evalId);
      if (!ev || normalizeToICS(ev.section) !== section) {
        setEvalId('');
      }
    }
  }, [section, sectionEvals, evalId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error('Message is required'); return; }
    setSubmitting(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const up = await uploadsApi.upload(f);
        uploaded.push(up.file_id);
      }
      await objectivesApi.createNote({
        exercise_id: exerciseId,
        objective_id: objective.objective_id,
        kpi_id: kpi?.kpi_id || null,
        eval_id: evalId || null,
        section: section || null,
        section_other: section === 'Others' ? sectionOther : null,
        role: role || null,
        message,
        attachments: uploaded,
      });
      toast.success('Note saved');
      setMessage(''); setSection(''); setSectionOther(''); setRole(''); setEvalId(''); setFiles([]);
      loadNotes();
    } catch (err) {
      toast.error(err?.detail || 'Failed to save note');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0" data-testid="kpi-notes-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="font-secondary">
            {kpi ? 'KPI Notes' : 'Objective Notes'}
          </DialogTitle>
          {objective && (
            <DialogDescription className="text-xs">
              Thread for Objective: <span className="font-medium">{objective.objective_code}</span>
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-3 space-y-3">
          {/* KPI context */}
          <div className="rounded-lg border p-3 bg-muted/40 space-y-1">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{kpi ? 'KPI' : 'Objective'}</div>
            <p className="text-sm whitespace-pre-wrap">{kpi ? kpi.kpi_text : objective?.objective_text || '-'}</p>
          </div>

          {/* Evaluation context when a section_evaluation is selected */}
          {currentEval && (
            <div className="rounded-lg border border-blue-400/40 bg-blue-500/5 p-3 space-y-2"
              data-testid="kpi-eval-context">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-blue-700 dark:text-blue-300">
                <ClipboardCheck className="w-3.5 h-3.5" />
                <span>{currentEval.section}</span>
                {currentEval.type && <span className="px-1.5 py-0.5 rounded bg-muted border text-[10px] normal-case">{currentEval.type}</span>}
              </div>
              {currentEval.evaluation_question && (
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">Functional Evaluation Question</div>
                  <p className="text-sm">{currentEval.evaluation_question}</p>
                </div>
              )}
              {currentEval.what_good_looks_like && (
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><Target className="w-3 h-3" /> What good looks like</div>
                  <p className="text-xs whitespace-pre-wrap">{currentEval.what_good_looks_like}</p>
                </div>
              )}
            </div>
          )}

          {/* Past notes thread */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">All Records</Label>
            {loading ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : notes.length === 0 ? (
              <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-3">No notes yet</div>
            ) : (
              notes.map(n => (
                <div key={n.note_id} className="border rounded-lg p-3 bg-card" data-testid={`kpi-note-${n.note_id}`}>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium">{n.user_name || 'User'}</span>
                      {n.section && (
                        <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30">
                          {n.section === 'Others' && n.section_other ? `Others: ${n.section_other}` : n.section}
                        </span>
                      )}
                      {n.role && <span className="px-1.5 py-0.5 rounded bg-muted border">{n.role}</span>}
                    </div>
                    <span className="text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm mt-2 whitespace-pre-wrap">{n.message}</p>
                  {!!(n.attachments || []).length && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {n.attachments.map((fid) => (
                        <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                          className="text-xs underline text-primary inline-flex items-center gap-1">
                          <Paperclip className="w-3 h-3" /> Attachment
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {readOnly ? (
          <div className="border-t bg-card px-6 py-3 shrink-0">
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </div>
        ) : (
        <form onSubmit={submit} className="border-t bg-card px-6 py-4 space-y-3 shrink-0">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">ICS Section</Label>
              <Select value={section} onValueChange={setSection}>
                <SelectTrigger className="h-9" data-testid="kpi-note-section-select">
                  <SelectValue placeholder="ICS Section…" />
                </SelectTrigger>
                <SelectContent>
                  {DEFAULT_SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role</Label>
              <Input value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. Section Chief"
                className="h-9" data-testid="kpi-note-role-input" />
            </div>
          </div>

          {/* V14.13.5 — Secondary picker shown only when the picked ICS
              section has matching imported evaluation slots. */}
          {evalsForSection.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs">Evaluation Question (optional)</Label>
              <Select value={evalId} onValueChange={setEvalId}>
                <SelectTrigger className="h-9" data-testid="kpi-note-eval-select">
                  <SelectValue placeholder="Pick a specific evaluation slot…" />
                </SelectTrigger>
                <SelectContent>
                  {evalsForSection.map(ev => (
                    <SelectItem key={ev.eval_id} value={ev.eval_id}>
                      {(ev.evaluation_question || ev.section || 'Evaluation').slice(0, 80)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currentEval?.what_good_looks_like && (
                <p className="text-[11px] text-muted-foreground italic mt-1">
                  <span className="font-medium not-italic">What good looks like: </span>
                  {currentEval.what_good_looks_like}
                </p>
              )}
            </div>
          )}

          {section === 'Others' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Specify &ldquo;Others&rdquo;</Label>
              <Input value={sectionOther} onChange={e => setSectionOther(e.target.value)}
                placeholder="e.g. HSE, External Affairs…" className="h-9"
                data-testid="kpi-note-section-other-input" />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Comments *</Label>
            <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} required
              placeholder="Record your observation…" data-testid="kpi-note-message" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Attachments (optional)</Label>
            <Input type="file" multiple onChange={e => setFiles(Array.from(e.target.files || []))}
              data-testid="kpi-note-attachments-input" />
            <p className="text-[10px] text-muted-foreground">Allowed: images/pdf/docx (max ~25MB each)</p>
          </div>

          <DialogFooter className="gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button type="submit" disabled={submitting} data-testid="kpi-note-send">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
